import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Trash2 } from 'lucide-react';
import expedienteService from '../services/expedienteService';
import { getClienteStatusColor, getExpedienteStatusColor } from '../utils/statusColors';
import CreateExpedienteModal from '../components/CreateExpedienteModal';
import ConfirmModal from '../components/ConfirmModal';
import './Expedientes.css';

const Expedientes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    cliente_status: '',
    expediente_status: '',
    sede: '',  // NUEVO FILTRO
    page: 1,
    limit: 50,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [statusEnums, setStatusEnums] = useState({ cliente_status: [], expediente_status: [], sedes: [] });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expedienteToDelete, setExpedienteToDelete] = useState(null);

  const canEdit = user?.role === 'admin' || user?.role === 'auditor';
  const canDelete = user?.role === 'admin';

  useEffect(() => {
    loadStatusEnums();
  }, []);

  useEffect(() => {
    loadExpedientes();
  }, [filters]);

  const loadStatusEnums = async () => {
    try {
      const data = await expedienteService.getStatusEnums();
      setStatusEnums(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadExpedientes = async () => {
    setLoading(true);
    try {
      const data = await expedienteService.getExpedientes(filters);
      setExpedientes(data.data || []);
      setTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (expediente, e) => {
    e.stopPropagation(); // Evitar que abra el expediente
    setExpedienteToDelete(expediente);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await expedienteService.deleteExpediente(expedienteToDelete._id);
      setShowDeleteConfirm(false);
      setExpedienteToDelete(null);
      loadExpedientes(); // Recargar lista
      // Opcional: mostrar notificación de éxito
    } catch (error) {
      console.error('Error eliminando expediente:', error);
      alert('Error al eliminar expediente: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="expedientes-container" data-testid="expedientes-page">
      <div className="expedientes-header">
        <div>
          <h1>Lista de Expedientes</h1>
          <p>Busca y filtra expedientes de auditoría</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="filters-grid">
          <div className="filter-input-wide">
            <label>Búsqueda</label>
            <input
              type="text"
              placeholder="Buscar por cédula, nombre o email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
              data-testid="search-input"
            />
          </div>

          <div className="filter-input">
            <label>Estatus Cliente</label>
            <select
              value={filters.cliente_status}
              onChange={(e) => setFilters(prev => ({ ...prev, cliente_status: e.target.value, page: 1 }))}
              data-testid="cliente-status-filter"
            >
              <option value="">Todos</option>
              {statusEnums.cliente_status.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="filter-input">
            <label>Estatus Expediente</label>
            <select
              value={filters.expediente_status}
              onChange={(e) => setFilters(prev => ({ ...prev, expediente_status: e.target.value, page: 1 }))}
              data-testid="expediente-status-filter"
            >
              <option value="">Todos</option>
              {statusEnums.expediente_status.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="filter-input">
            <label>Sede</label>
            <select
              value={filters.sede}
              onChange={(e) => setFilters(prev => ({ ...prev, sede: e.target.value, page: 1 }))}
              data-testid="sede-filter"
            >
              <option value="">Todas las Sedes</option>
              {(statusEnums.sedes || []).map(sede => (
                <option key={sede} value={sede}>{sede}</option>
              ))}
            </select>
          </div>
        </div>

        {canEdit && (
          <div className="filter-actions">
            <button onClick={() => setShowCreateModal(true)} className="create-btn" data-testid="create-expediente-button">
              + Crear Expediente
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="table-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando expedientes...</p>
          </div>
        ) : expedientes.length === 0 ? (
          <div className="empty-state">
            <p className="empty-title">No se encontraron expedientes</p>
            <p className="empty-text">Intenta ajustar los filtros de búsqueda</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="expedientes-table" data-testid="expedientes-table">
                <thead>
                  <tr>
                    <th>Cédula</th>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Contrato</th>
                    <th>Sede</th>
                    <th>Cliente</th>
                    <th>Expediente</th>
                    {canDelete && <th style={{ width: '80px', textAlign: 'center' }}>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {expedientes.map((exp) => (
                    <tr
                      key={exp.id}
                      onClick={() => navigate(`/expedientes/${exp.id}`)}
                      data-testid={`expediente-row-${exp.id}`}
                    >
                      <td>{exp.informacion?.cedula || '-'}</td>
                      <td>{exp.informacion?.nombre} {exp.informacion?.apellido}</td>
                      <td>{exp.informacion?.email || '-'}</td>
                      <td>{exp.informacion?.contrato || '-'}</td>
                      <td>{exp.informacion?.sede || '-'}</td>
                      <td>
                        <span className={`badge ${getClienteStatusColor(exp.cliente_status)}`}>
                          {exp.cliente_status}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getExpedienteStatusColor(exp.expediente_status)}`}>
                          {exp.expediente_status}
                        </span>
                      </td>
                      {canDelete && (
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={(e) => handleDeleteClick(exp, e)}
                            style={{
                              padding: '8px 10px',
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              borderRadius: '6px',
                              color: '#ef4444',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            title="Eliminar expediente"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">Página {filters.page} de {totalPages}</div>
                <div className="pagination-buttons">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={filters.page === 1}
                    data-testid="prev-page-button"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={filters.page >= totalPages}
                    data-testid="next-page-button"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <CreateExpedienteModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); loadExpedientes(); }}
          statusEnums={statusEnums}
        />
      )}

      {showDeleteConfirm && expedienteToDelete && (
        <ConfirmModal
          isOpen={showDeleteConfirm}
          title="Eliminar Expediente"
          message={`¿Estás seguro de que deseas eliminar el expediente de ${expedienteToDelete.informacion?.nombre} ${expedienteToDelete.informacion?.apellido} (Cédula: ${expedienteToDelete.informacion?.cedula})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setExpedienteToDelete(null);
          }}
          type="danger"
        />
      )}
    </div>
  );
};

export default Expedientes;
